import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { initTerminalRuntimeDefaults } from "../Constants/terminalRuntimeConfig";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];
const auth = getAuth(app);

const initializeAuth = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    console.error("Error setting Firebase persistence:", error);
  }
};

initializeAuth();

export const authReady = new Promise((resolve) => {
  const init = async () => {
    try {
      await setPersistence(auth, browserLocalPersistence);

      onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const idToken = await user.getIdToken(true);
            localStorage.setItem("accessToken", idToken);
            localStorage.setItem("userUid", user.uid);
            initTerminalRuntimeDefaults();
            resolve(user);
          } catch (error) {
            console.error("Token refresh failed:", error);
            resolve(null);
          }
        } else {
          console.warn("No user detected.");
          resolve(null);
        }
      });
    } catch (err) {
      console.error("Auth initialization failed:", err);
      resolve(null);
    }
  };

  init();
});

export const firebaseRegister = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    console.error("Firebase Registration Error:", error.message);
    throw error;
  }
};

export const firebaseLogin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const idToken = await userCredential.user.getIdToken();
    window.localStorage.setItem("accessToken", idToken);
    window.localStorage.setItem("userUid", userCredential.user.uid);
    return idToken;
  } catch (error) {
    console.error("Firebase Login Error:", error.message);
    throw error;
  }
};

export const firebaseResetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Password reset failed:", error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    const idToken = await result.user.getIdToken();
    console.log("[signInWithGoogle] Got ID Token:", idToken);
    return idToken;
  } catch (error) {
    console.error("[signInWithGoogle] Failed:", error);
    throw error;
  }
};

export const signInWithApple = async () => {
  try {
    const provider = new OAuthProvider("apple.com");
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    return idToken;
  } catch (error) {
    console.error("Apple Login Failed:", error);
    throw error;
  }
};

export const firebaseLogout = async () => {
  try {
    await auth.signOut();
    window.localStorage.removeItem("accessToken");
    window.sessionStorage.removeItem("accessToken");
    window.localStorage.removeItem("userId");
  } catch (error) {
    console.error("Logout failed:", error);
    throw error;
  }
};

export { auth };
